import React, { cloneElement } from "react";

import FilterIcon from "@uui/FilterLines";
import { difference } from "lodash/fp";

import { Button, FormControlLabel } from "@mui/material";

import { FilterCount } from "@app/analytics/MeetingReport/styles";
import type { SessionHighlight } from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Checkbox from "@rui/Checkbox";
import Menu, { MenuHeader, MenuBody } from "@rui/Menu";
import { Column, Row } from "@rui/Stack";

export const HIGHLIGHT_FILTERS: {
  label: string;
  types: SessionHighlight["type"][];
}[] = [
  { label: "Reactions", types: ["positive", "negative"] },
  { label: "Topics", types: ["topic"] },
  { label: "Action Items", types: ["action_item"] },
  { label: "Key Questions", types: ["key_question"] },
];

const HighlightFilterMenu = ({
  children,
  excludedTypes,
  filterCount,
  onFilterChange,
}: {
  children: React.ReactElement;
  excludedTypes: SessionHighlight["type"][];
  filterCount: number;
  onFilterChange: (types: SessionHighlight["type"][]) => void;
}) => {
  const { anchorElement, onClick, open, onClose } = useAnchorElement();

  const hasFilters = filterCount > 0;

  const handleFilterChange =
    (types: SessionHighlight["type"][]) =>
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        onFilterChange(difference(excludedTypes, types));
      } else {
        onFilterChange([...excludedTypes, ...types].sort());
      }
    };

  const handleClearFilters = () => onFilterChange([]);
  const openMenuButton = cloneElement(children, { onClick });

  return (
    <>
      {openMenuButton}
      <Menu
        open={open}
        onClose={onClose}
        anchorEl={anchorElement}
        disablePadding
      >
        <MenuHeader variant="apart">
          <Row spacing={1} alignItems="center">
            <FilterIcon />
            <span>Filters</span>
            {hasFilters && <FilterCount>{filterCount}</FilterCount>}
          </Row>
          {hasFilters && (
            <Button onClick={handleClearFilters} sx={{ p: "2px 4px" }}>
              Clear all
            </Button>
          )}
        </MenuHeader>
        <MenuBody sx={{ minWidth: "300px" }}>
          <Column>
            {HIGHLIGHT_FILTERS.map((filter) => {
              const isChecked = filter.types.every(
                (type) => !excludedTypes.includes(type)
              );
              return (
                <FormControlLabel
                  key={filter.label}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={handleFilterChange(filter.types)}
                    />
                  }
                  label={filter.label}
                />
              );
            })}
          </Column>
        </MenuBody>
      </Menu>
    </>
  );
};

export default HighlightFilterMenu;
